import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

const RECAPTCHA_SITE_KEY = '6Ldu414sAAAAAPlnpkSoxceCF6Eo9esUj2nRIzEM';

import Header from './components/Header';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import Home from './pages/Home';
import About from './pages/About';
import Buyers from './pages/Buyers';
import Sellers from './pages/Sellers';
import Listings from './pages/Listings';
import PropertyDetail from './pages/PropertyDetail';
import Search from './pages/Search';
import Contact from './pages/Contact';
import TermsOfUse from './pages/TermsOfUse';
import PrivacyPolicy from './pages/PrivacyPolicy';

import './styles/style.css';

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Disable browser's scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
  }, [pathname]);

  return null;
}

function SafeAreaPadding() {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const safePadding = params.get('safe_padding');

    const applyPadding = (padding: number) => {
      document.body.classList.add('reel-safe-mode');
      document.documentElement.style.setProperty('--reel-safe-padding', `${padding}px`);
      // Top padding is larger to account for phone notch/dynamic island
      document.documentElement.style.setProperty('--reel-safe-top', `${padding + 60}px`);
    };

    if (safePadding) {
      // Store in sessionStorage so it persists across navigations
      const padding = parseInt(safePadding, 10) || 40;
      sessionStorage.setItem('reel-safe-padding', String(padding));
      applyPadding(padding);
    } else {
      // Check sessionStorage for existing safe padding setting
      const storedPadding = sessionStorage.getItem('reel-safe-padding');
      if (storedPadding) {
        applyPadding(parseInt(storedPadding, 10));
      } else {
        document.body.classList.remove('reel-safe-mode');
        document.documentElement.style.removeProperty('--reel-safe-padding');
        document.documentElement.style.removeProperty('--reel-safe-top');
      }
    }
  }, [location.search, location.pathname]);

  return null;
}

function App() {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
      <Router>
        <ScrollToTop />
        <SafeAreaPadding />
        <Header />
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/buyers" element={<Buyers />} />
            <Route path="/sellers" element={<Sellers />} />
            <Route path="/listings" element={<Listings />} />
            <Route path="/listings/:idxID/:listingID/:slug?" element={<PropertyDetail />} />
            <Route path="/search" element={<Search />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms-of-use" element={<TermsOfUse />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          </Routes>
        </main>
        <Footer />
        <CookieConsent />
      </Router>
    </GoogleReCaptchaProvider>
  );
}

export default App;
